"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocationFulfillmentConfigUpdateSchema = exports.LocationFulfillmentConfigSchema = exports.NoGoZoneSchema = exports.ServiceStatesSchema = exports.MethodStatesSchema = exports.UsStateCodeSchema = exports.PickupConfigSchema = exports.ShippingConfigSchema = exports.ShippingCarrierSchema = exports.LocalDeliveryConfigSchema = exports.FeeTableSchema = exports.LocalDeliveryMethodSchema = exports.DayOfWeekSchema = void 0;
const zod_1 = require("zod");
// Day-of-week tokens used by local-delivery scheduling windows.
exports.DayOfWeekSchema = zod_1.z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
// How a location decides whether a customer is inside its local-delivery zone.
// Launch methods: radius-km, cities, zipcodes (drive-time/polygon are future).
exports.LocalDeliveryMethodSchema = zod_1.z.enum([
    'radius-km',
    'drive-time-min',
    'cities',
    'zipcodes',
    'polygon',
]);
// fee may be a flat number OR a tiered table keyed by distance bucket (future).
exports.FeeTableSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.number());
exports.LocalDeliveryConfigSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    method: exports.LocalDeliveryMethodSchema,
    // number for radius-km/drive-time-min; string[] for cities/zipcodes/polygon coords.
    value: zod_1.z.union([zod_1.z.number(), zod_1.z.array(zod_1.z.string())]),
    fee: zod_1.z.union([zod_1.z.number(), exports.FeeTableSchema]),
    minOrderValue: zod_1.z.number().nonnegative().optional(),
    estimatedMinutes: zod_1.z.number().nonnegative(),
    cutoffTime: zod_1.z.string().regex(/^\d{2}:\d{2}$/).optional(),
    daysOfWeek: zod_1.z.array(exports.DayOfWeekSchema).optional(),
    // After the same-day cut-off passes (or on a non-delivery day per daysOfWeek), how the
    // store handles the order: 'next_day' keeps local delivery but schedules it for the next
    // open delivery day; 'shipping_only' drops delivery and routes to shipping. Optional with a
    // reader default of 'next_day' (same pattern as pricingMode below) so existing rows parse
    // unchanged, no store loses delivery on deploy, and existing config literals stay valid.
    // Enforced by ecommerce-service delivery-window.ts + both fulfillment routers.
    afterCutoffMode: zod_1.z.enum(['next_day', 'shipping_only']).optional(),
    // Pricing mode for the delivery fee. Reader default = 'flat' when absent (back-compat).
    pricingMode: zod_1.z.enum(['free', 'flat', 'per_mile']).optional(),
    // $/mile, used only when pricingMode === 'per_mile'.
    perMileRate: zod_1.z.number().nonnegative().optional(),
    // Delivery is free when the cart subtotal is >= this amount.
    freeOverAmount: zod_1.z.number().nonnegative().optional(),
});
exports.ShippingCarrierSchema = zod_1.z.enum(['usps', 'ups', 'fedex', 'dhl']);
exports.ShippingConfigSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    carriers: zod_1.z.array(exports.ShippingCarrierSchema).default([]),
    // flat / weight-based table; carrier-API rating is future.
    rates: zod_1.z.record(zod_1.z.string(), zod_1.z.number()).default({}),
    // Owner-set manual flat shipping fee for out-of-delivery-area (ship) orders.
    // Real carrier rates come later; until then this single number is the shipping charge.
    // Optional (not .default) so existing config literals stay valid; readers treat undefined as 0.
    flatRate: zod_1.z.number().nonnegative().optional(),
    freeOver: zod_1.z.number().nonnegative().optional(),
    // 'free' (no charge) | 'flat' (uses flatRate). Reader default = 'flat' when absent.
    pricingMode: zod_1.z.enum(['free', 'flat']).optional(),
    // 'domestic' | 'international' | explicit state/region codes the location ships to.
    zones: zod_1.z.union([zod_1.z.enum(['domestic', 'international']), zod_1.z.array(zod_1.z.string())]).optional(),
});
exports.PickupConfigSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    estimatedMinutes: zod_1.z.number().nonnegative(),
    // mirrors APosLocation.operatingHours shape; optional so pickup can inherit location hours.
    hours: zod_1.z.record(zod_1.z.string(), zod_1.z.object({
        open: zod_1.z.string().regex(/^\d{2}:\d{2}$/),
        close: zod_1.z.string().regex(/^\d{2}:\d{2}$/),
        closed: zod_1.z.boolean().default(false),
    })).optional(),
});
// USPS 2-letter code, uppercase (e.g. "VA").
exports.UsStateCodeSchema = zod_1.z.string().regex(/^[A-Z]{2}$/);
// Per-method allow-list. `states` = ADDITIONAL states beyond the home state
// (home is always allowed and never stored here). `allStates` = nationwide.
exports.MethodStatesSchema = zod_1.z.object({
    allStates: zod_1.z.boolean().default(false),
    states: zod_1.z.array(exports.UsStateCodeSchema).default([]),
    // When true, the store's home state is NOT auto-served — the explicit `states`
    // list (or `allStates`) is the sole source of truth. Absent/false keeps the
    // legacy "home always allowed" safeguard, so existing configs are unaffected.
    excludeHome: zod_1.z.boolean().optional(),
});
exports.ServiceStatesSchema = zod_1.z.object({
    delivery: exports.MethodStatesSchema,
    shipping: exports.MethodStatesSchema,
});
// A "no-go" area a store will NOT deliver to (delivery only). Coords are [lng, lat].
exports.NoGoZoneSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({
        id: zod_1.z.string(), name: zod_1.z.string().optional(),
        type: zod_1.z.literal('circle'),
        center: zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()]),
        radiusKm: zod_1.z.number().positive(),
    }),
    zod_1.z.object({
        id: zod_1.z.string(), name: zod_1.z.string().optional(),
        type: zod_1.z.literal('polygon'),
        coordinates: zod_1.z.array(zod_1.z.tuple([zod_1.z.number(), zod_1.z.number()])).min(3),
    }),
]);
// Additive sub-object stored on the locations entity (agenticpos-pos-dynamodb-locations-{stage}).
// `doorDashEnabled` is the owner-level toggle (one platform-held DoorDash account).
exports.LocationFulfillmentConfigSchema = zod_1.z.object({
    localDelivery: exports.LocalDeliveryConfigSchema,
    shipping: exports.ShippingConfigSchema,
    pickup: exports.PickupConfigSchema,
    doorDashEnabled: zod_1.z.boolean().default(false),
    serviceStates: exports.ServiceStatesSchema.optional(),
    noGoZones: zod_1.z.array(exports.NoGoZoneSchema).optional(),
});
// PATCH-friendly partial used by the platform-app "Online Fulfillment" tab.
exports.LocationFulfillmentConfigUpdateSchema = zod_1.z.object({
    localDelivery: exports.LocalDeliveryConfigSchema.partial().optional(),
    shipping: exports.ShippingConfigSchema.partial().optional(),
    pickup: exports.PickupConfigSchema.partial().optional(),
    doorDashEnabled: zod_1.z.boolean().optional(),
    serviceStates: exports.ServiceStatesSchema.optional(),
    noGoZones: zod_1.z.array(exports.NoGoZoneSchema).optional(),
});
//# sourceMappingURL=location-fulfillment.schema.js.map